import {IHttpService, ILogService, IPromise, IQService} from "angular";
import {Application} from "../model/Application";
import {Assessment} from "../model/Assessment";
import {CourseOfferStatus} from "../model/CourseOfferStatus";
import {PriorCourse} from "../model/PriorCourse";
import {MonashCourse} from "../model/MonashCourse";
import {PriorCourseUnit} from "../model/PriorCourseUnit";
import {Applicant} from "../model/Applicant";
import {ApplicationId} from "../model/ApplicationId";
import {LogService} from "../log/log.service";

export class ApplicationService {

    /* @ngInject */
    constructor(private $http: IHttpService,
                private $log: ILogService,
                private $q: IQService,
                private logService: LogService) {
    }

    public getStudentDetailsById(id: string): IPromise<Applicant> {
        return this.$http.get(`/api/student/${id}`).then((resp) => {
            this.$log.debug("getStudentDetailsById: ", resp.data);
            return resp.data as Applicant;
        });
    }
    public getAdminStudentDetailsById(id: string): IPromise<Applicant> {
        const url = `/system/get-applicant/${id}`;
        return this.$http.get(url).then((resp) => {
            this.$log.debug("getAdminStudentDetailsById: ", resp.data);
            return resp.data as Applicant;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "GET", url, {id: id});
            return this.$q.reject(error);
        });
    }

    // student credit request
    public getStudentCreditRequests(): IPromise<Application> {
        return this.$http.get("/api/student-credit-request")
            .then((resp) => {
                return resp.data ? resp.data as Application[] : [];
            }, (error) => {
                this.$log.warn("Applications not found for user:", error);
                return null;
            });
    }

    public getDrafts(): IPromise<Application[]> {
        return this.$http.get(`/api/student-credit-request/drafts`).then((resp) => {
            this.$log.debug("applications: ", resp.data);
            return resp.data as Application[];
        });
    }

    public deleteDraft(studentCreditRequestId: string): IPromise<Application> {
        return this.$http.post<Application>(`/api/student-credit-request/${studentCreditRequestId}/delete`, {})
            .then((resp) => resp.data);
    }

    public getStudentCreditRequestById(id: string): IPromise<Application> {
        return this.$http.get(`/api/student-credit-request/${id}`).then((resp) => {
            this.$log.debug("application: ", resp.data);
            return resp.data as Application;
        });
    }

    public saveStudentCreditRequestAsDraft(application: Application): IPromise<Application> {
        const url = "/api/student-credit-request/draft";
        return this.$http.post(url, application)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", url, application);
                return this.$q.reject(error);
            });
    }

    public submitStudentCreditRequest(application: Application): IPromise<Application> {
        const url = "/api/student-credit-request/submit";
        return this.$http.post(url, application)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", url, application);
                return this.$q.reject(error);
            });
    }

    public removeStudentCreditRequestSupportingDocument(applicationId: string, attachmentId: string): IPromise<any> {
        return this.$http.delete(`/api/student-credit-request/${applicationId}/supporting-documents/${attachmentId}`).then((resp) => {
            return resp.data;
        });
    }

    public removeStudentCreditRequestUnitSupportingDocument(applicationId: string, unitId: string, attachmentId: string): IPromise<any> {
        return this.$http.delete(`/api/student-credit-request/${applicationId}/prior-units/${unitId}/supporting-documents/${attachmentId}`).then((resp) => {
            return resp.data;
        });
    }

    public getStudentCreditRequestApplications(id: string): IPromise<Application[]> {
        return this.$http.get(`/api/student-credit-request/${id}/applications`).then((resp) => {
            this.$log.debug("application: ", resp.data);
            return resp.data as Application[];
        });
    }

    // student credit applications

    public getStudentCreditApplicationById(id: string): IPromise<Application> {
        return this.$http.get(`/api/student-credit-application/${id}`).then((resp) => {
            this.$log.debug("application: ", resp.data);
            return resp.data ? resp.data as Application : null;
        });
    }

    public getStudentCreditApplicationsForStudentAndFaculty(studentId: string, facultyCode: string): IPromise<Application[]> {
        return this.$http.get(`/api/student-credit-application/faculty/${facultyCode}/applicant/${studentId}`)
            .then((resp) => {
            this.$log.debug("applications: ", resp.data);
            return resp.data ? resp.data as Application[] : [];
        });
    }

    public getStudentCreditApplications(filter?): IPromise<Application[]> {
        return this.$http.post("/api/student-credit-application/search", filter)
            .then((resp) => {
                const applications = resp.data;
                this.$log.debug("Applications found:", applications);

                return applications as Application[];
            });
    }

    public getStudentCreditApplicationIds(filter?): IPromise<ApplicationId[]> {
        return this.$http.post("/api/student-credit-application-id/search", filter)
            .then((resp) => {
                const applicationIds = resp.data;
                this.$log.debug("Applications found:", applicationIds);

                return applicationIds as ApplicationId[];
            });
    }

    public getLightStudentCreditApplications(filter?): IPromise<Application[]> {
        return this.$http.post("/api/light-student-credit-application/search", filter)
            .then((resp) => {
                const applications = resp.data;
                this.$log.debug("Applications found:", applications);

                return applications as Application[];
            });
    }

    public saveSupportingDocuments(id: string, documents: any[]): IPromise<Application> {
        return this.$http.post(`/api/student-credit-request/${id}/save-supporting-documents`, documents)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-request/${id}/save-supporting-documents`, documents);
                return this.$q.reject(error);
            });
    }

    public saveInternalUseOnlyDocuments(id: string, documents: any[]): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${id}/internal-use-only-documents`, documents)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-application/${id}/internal-use-only-documents`, documents);
                return this.$q.reject(error);
            });
    }

    public uploadCourseDocumentByStaff(id: string, documents: any[], courseCode: string): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${id}/course-document-uploaded-by-staff/${courseCode}`, documents)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-application/${id}/course-document-uploaded-by-staff`, documents);
                return this.$q.reject(error);
            });
    }

    public declineStudentCreditApplicationByFacultyAdmin(application: Application): IPromise<Application> {
        const data = {
            outcomeComment: application.outcomeComment
        };
        return this.$http.post(`/api/student-credit-application/${application.id}/decline`, data)
            .then((resp) => resp.data as Application);
    }


    public resetApplicationStatus(application: Application): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/reset-status`, {})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", `/api/student-credit-application/${application.id}/reset-status`, application);
                return this.$q.reject(error);
            });
    }

    public reopenStudentCreditApplicationSummary(application: Application): IPromise<Application> {
        const data = {
            outcomeComment: application.outcomeComment
        };
        return this.$http.post(`/api/student-credit-application/${application.id}/reopen-summary`, data)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", `/api/student-credit-application/${application.id}/reopen-summary`, application);
                return this.$q.reject(error);
            });
    }

    public updateStudentEmail(application: Application, email: string): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/update/email`, {email})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-application/${application.id}/update/email`, {application: application, email: email});
                return this.$q.reject(error);
            });

    }

    public updateStudentPhone(application: Application): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/update/phone`, application)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", `/api/student-credit-application/${application.id}/update/phone`, application);
                return this.$q.reject(error);
            });
    }

    public updateStudentCoursePreference(application: Application, courses: MonashCourse[]): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/update/course-preference`, courses)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-application/${application.id}/update/course-preference`, {application: application, courses: courses});
                return this.$q.reject(error);
            });
    }

    public archiveStudentCreditApplication(application: Application): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/archive`, {})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", `/api/student-credit-application/${application.id}/archive`, application);
                return this.$q.reject(error);
            });
    }

    public unarchiveStudentCreditApplication(application: Application): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/unarchive`, {})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", `/api/student-credit-application/${application.id}/unarchive`, application);
                return this.$q.reject(error);
            });
    }

    public processOffline(application: Application): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/process-offline`, {})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", `/api/student-credit-application/${application.id}/process-offline`, application);
                return this.$q.reject(error);
            });

    }


    public reopenStudentCreditApplication(application: Application, comments: string, isEmailSent: boolean): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/reopen`, {comments: comments, isEmailSent: isEmailSent})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", `/api/student-credit-application/${application.id}/reopen`, application);
                return this.$q.reject(error);
            });
    }

    public reallocateStudentCreditApplicationFaculty(application: Application, owningFacultyCode: string): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/owning-faculty-code`, {owningFacultyCode: owningFacultyCode})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-application/${application.id}/owning-faculty-code`, {application: application, owningFacultyCode: owningFacultyCode});
                return this.$q.reject(error);
            });
    }

    public updateStudentCreditApplicationOutcomeComment(id: string, outcomeComment: string) {
        return this.$http.put(`/api/student-credit-application/${id}/outcome-comment`, {outcomeComment})
            .then((resp) => resp.data)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-application/${id}/outcome-comment`, {id: id});
                return this.$q.reject(error);
            });
    }

    public approveStudentCreditApplicationByFacultyAdmin(application: Application, personUsername: string): IPromise<Application> {
        const data = {
            authorisingUsername: personUsername,
            outcomeComment: application.outcomeComment,
            adjustedCourseDuration: application.adjustedCourseDuration,
            govCreditBasis: application.govCreditBasis
        };
        return this.$http.post(`/api/student-credit-application/${application.id}/approve`, data)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-application/${application.id}/approve`, {application: application, personUsername: personUsername});
                return this.$q.reject(error);
            });
    }

    public removeStudentCreditApplicationInternalUseOnlyDocument(id: string, attachmentId: string): IPromise<any> {
        return this.$http.delete(`/api/student-credit-application/${id}/internal-use-only-documents/${attachmentId}`).then((resp) => {
            return resp.data;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "DELETE", `/api/student-credit-application/${id}/internal-use-only-documents/${attachmentId}`, {id: id, attachmentId: attachmentId});
            return this.$q.reject(error);
        });
    }

    public removeCourseDocuments(id: string, courseCode: string, attachmentId: string): IPromise<any> {
        return this.$http.delete(`/api/student-credit-application/${id}/course-document-uploaded-by-staff/${courseCode}/${attachmentId}`).then((resp) => {
            return resp.data;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "DELETE", `/api/student-credit-application/${id}/course-document-uploaded-by-staff/${courseCode}/${attachmentId}`,
                {id: id, courseCode: courseCode, attachmentId: attachmentId});
            return this.$q.reject(error);
        });
    }

    public getCourseOfferStatusByApplicationId(id: string): IPromise<CourseOfferStatus[]> {
        return this.$http.get(`/api/student-credit-application/${id}/course-offer-status`).then((resp: any) => {
            return resp.data as CourseOfferStatus[];
        });
    }

    public releaseToFacultyAdmin(id: string): IPromise<Application> {
        return this.$http.put(`/api/student-credit-application/${id}/release-to-faculty-admin`, {})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "PUT", `/api/student-credit-application/${id}/release-to-faculty-admin`, {id: id});
                return this.$q.reject(error);
            });
    }

    public getStudentCreditApplicationHistoryByApplicationId(id: string): IPromise<any[]> {
        return this.$http.get(`/api/student-credit-application/${id}/history`)
            .then((resp) => resp.data as any[]);
    }

    /***
     * Add the view history from staff side
     * @param id application id
     */
    public addViewHistoryByStaff(id: string): IPromise<any[]> {
        return this.$http.post(`/api/student-credit-application/${id}/add-view-audit`, {})
            .then((resp) => resp.data as any[]);
    }


    public getMyAccessingApplications(): IPromise<Application[]> {
        return this.$http.get(`/api/me/student-credit-application`)
            .then((resp) => resp.data as Application[]);
    }

    public acceptByStudent(application: Application, assessmentIds: string[]): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/accept-offer`, assessmentIds)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "POST", `/api/student-credit-application/${application.id}/accept-offer`, {application: application, assessmentIds: assessmentIds});
                return this.$q.reject(error);
            });
    }

    public declineByStudent(application: Application): IPromise<Application> {
        return this.$http.post(`/api/student-credit-application/${application.id}/decline-offer`, {})
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logError(error, "POST", `/api/student-credit-application/${application.id}/decline-offer`, application);
                return this.$q.reject(error);
            });
    }

    public updatePriorStudies(id: string, priorStudies: PriorCourse[], assessPriorStudies: boolean): IPromise<Application> {
        const data = {
            assessPriorStudies,
            priorStudies
        };
        this.$log.log(priorStudies);
        this.$log.log(id);
        return this.$http.put(`/api/student-credit-application/${id}/prior-studies`, data)
            .then((resp) => {
                this.$log.log(resp);
                return resp.data as Application;
            })
            .catch((error) => {
                this.logService.logErrorOfAny(error, "PUT", `/api/student-credit-application/${id}/prior-studies`, data);
                return this.$q.reject(error);
            });
    }


    public updatePriorStudiesUnitCode(id: string, unit: PriorCourseUnit): IPromise<Application> {
        return this.$http.put(`/api/student-credit-application/${id}/prior-studies/units/${unit.unitId}`, unit)
            .then((resp) => resp.data as Application)
            .catch((error) => {
                this.logService.logErrorOfAny(error, "PUT", `/api/student-credit-application/${id}/prior-studies/units/${unit.unitId}`, {id: id, unit: unit});
                return this.$q.reject(error);
            });
    }


    // Assessments

    public getApprovedAssessmentsByStudentCreditRequestId(applicationId: string): IPromise<Assessment[]> {
        return this.$http.get(`/api/application/student/${applicationId}/assessments`).then((resp) => {
            return resp.data as Assessment[];
        });
    }

    public getAssessmentsByApplicationId(applicationId: string): IPromise<Assessment[]> {
        return this.$http.get(`/api/application/${applicationId}/assessments`).then((resp) => {
            return resp.data as Assessment[];
        });
    }

    public getAssessmentHistoriesByApplicationIdAndAssessmentId(applicationId: string, assessmentId: string) {
        return this.$http.get(`/api/applications/${applicationId}/assessments/${assessmentId}/histories`).then((resp) => {
            return resp.data as Application;
        });
    }

    public saveAssessment(application: Application, assessment: Assessment): IPromise<Assessment> {
        return this.$http.post(`/api/application/${application.id}/assessments`, assessment).then((resp) => {
            return resp.data as Assessment;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "POST", `/api/application/${application.id}/assessments`, {application: application, assessment: assessment});
            return this.$q.reject(error);
        });
    }

    public approveAssessment(application: Application, assessment: Assessment): IPromise<Assessment> {
        return this.$http.post(`/api/application/${application.id}/assessments/${assessment.id}/approve`, {}).then((resp) => {
            return resp.data as Assessment;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "POST", `/api/application/${application.id}/assessments/${assessment.id}/approve`, {application: application, assessment: assessment});
            return this.$q.reject(error);
        });
    }

    public rejectAssessment(application: Application, assessment: Assessment): IPromise<Assessment> {
        return this.$http.post(`/api/application/${application.id}/assessments/${assessment.id}/reject`, {}).then((resp) => {
            return resp.data as Assessment;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "POST", `/api/application/${application.id}/assessments/${assessment.id}/reject`, {application: application, assessment: assessment});
            return this.$q.reject(error);
        });
    }

    public resetAssessment(application: Application, assessment: Assessment): IPromise<Assessment> {
        return this.$http.post(`/api/application/${application.id}/assessments/${assessment.id}/reset`, {}).then((resp) => {
            return resp.data as Assessment;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "POST", `/api/application/${application.id}/assessments/${assessment.id}/reset`, {application: application, assessment: assessment});
            return this.$q.reject(error);
        });
    }

    public removeAssessment(application: Application, assessment: Assessment): IPromise<Assessment> {
        return this.$http.delete(`/api/application/${application.id}/assessments/${assessment.id}`, {}).then((resp) => {
            return resp.data as Assessment;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "DELETE", `/api/application/${application.id}/assessments/${assessment.id}`,
                                 {application: application, assessment: assessment});
            return this.$q.reject(error);
        });
    }

    public inviteCollaboratorsToAssessments(applicationId: string, assessmentIds: string[],
                                            emailAddresses: string[], comment: string): IPromise<Assessment[]> {
        const assessmentInvites = {
            assessmentIds: assessmentIds,
            newCollaborators: emailAddresses,
            comment: comment
        };

        return this.$http.post(`/api/application/${applicationId}/assessments/invite`, assessmentInvites).then((resp) => {
            return resp.data as Assessment[];
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "POST", `/api/application/${applicationId}/assessments/invite`, assessmentInvites);
            return this.$q.reject(error);
        });
    }

    public inviteCollaboratorsToAssessment(applicationId: string, assessmentId: string,
                                           emailAddresses: string[], comment: string): IPromise<Assessment> {
        const assessmentInvites = {
            assessmentIds: [assessmentId],
            newCollaborators: emailAddresses,
            comment: comment
        };

        return this.$http.post(`/api/application/${applicationId}/assessments/${assessmentId}/invite`, assessmentInvites).then((resp) => {
            return resp.data as Assessment;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "POST", `/api/application/${applicationId}/assessments/${assessmentId}/invite`, assessmentInvites);
            return this.$q.reject(error);
        });
    }

    public removeCollaboratorFromAssessment(application: Application, assessment: Assessment,
                                            collaborator: string): IPromise<Assessment> {
        return this.$http.delete(`/api/application/${application.id}/assessments/${assessment.id}/collaborators/${collaborator}`).then((resp) => {
            return resp.data as Assessment;
        }).catch((error) => {
            this.logService.logErrorOfAny(error, "DELETE", `/api/application/${application.id}/assessments/${assessment.id}/collaborators/${collaborator}`,
                { application: application, assessment: assessment, collaborator: collaborator });
            return this.$q.reject(error);
        });
    }

    public loadAssessmentforAssessor(applicationId: string, base64Assessor: string): IPromise<Assessment> {
        return this.$http.get(`/api/application/${applicationId}/assessments/assessor/${base64Assessor}`).then((resp) => {
            return resp.data as Assessment;
        });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/main/client/app/shared/application/application.service.ts